import {
    defineElement,
    FC,
    onDidLoad,
    useElement,
    useElements,
    useListen,
    useProp,
} from '@atomify/hooks';
import { rafThrottle } from '@bpd-library/utilities';
import { elements } from '@source/utilities/dom';
import { HEADER_HEIGHT, HEADER_HEIGHT_MOBILE } from '@utilities/constants/css-constants';
import { deviceWidth } from '@utilities/media-query';

export interface HotspotImageElement extends HTMLElement {
    currentHotspotId: string | null;
    containerPosition: number;
}

const HAS_OPEN_MODAL_CLASS = 'hotspot-image--has-open-modal';
const MODAL_HTML_CLASS = 'modal--is-open';
const MODAL_OPEN_CLASS = 'hotspot-image__modal--open';
const DISABLE_CONTROLS_CLASS = 'hotspot-image--disable-controls';
const SCROLL_INCREMENT = 100;
const ANIMATION_DURATION = 200; // Match CSS transition duration

const getDeviceHeaderHeight = () =>
    deviceWidth.isMobilePlusAndSmaller() ? HEADER_HEIGHT_MOBILE : HEADER_HEIGHT;

const getSafeViewportDimensions = () => ({
    width: window.innerWidth,
    height: window.innerHeight * 0.9 - getDeviceHeaderHeight(),
});

const HotspotImage: FC<HotspotImageElement> = ({ element }) => {
    const [, setCurrentHotspotId, watchCurrentHotspotId] = useProp<string | null>(
        'currentHotspotId',
        null,
    );

    const viewport = useElement<HTMLDivElement>('[js-hook-hotspot-image-viewport]');
    const contentContainer = useElement<HTMLDivElement>(
        '[js-hook-hotspot-image-content-container]',
    );
    const imageElement = useElement<HTMLImageElement>('[js-hook-hotspot-image-image]');
    const hotspotButtons = useElements<HTMLButtonElement[]>(
        '[js-hook-hotspot-image-hotspot-button]',
    );
    const hotspotOverlay = useElement<HTMLDivElement>('[js-hook-hotspot-image-hotspot-overlay]');
    const infoDialogs = useElements<HTMLDialogElement[]>('[js-hook-hotspot-image-info]');
    const leftControl = useElement<HTMLButtonElement>('[js-hook-hotspot-image-left]');
    const rightControl = useElement<HTMLButtonElement>('[js-hook-hotspot-image-right]');
    const modalCloseButtons = useElements<HTMLButtonElement[]>(
        '[js-hook-hotspot-image-modal-close]',
    );

    let activeDialog: HTMLDialogElement | null = null;
    let imageNaturalWidth: number = 0;
    let imageNaturalHeight: number = 0;
    let containerScrollPosition: number = 0;

    onDidLoad(initializeImageDimensions);

    useListen(window, 'resize', rafThrottle(resizeAndAlignImage));

    watchCurrentHotspotId((newId, oldId) => {
        if (newId) {
            openHotspotModal(newId);
        } else if (oldId) {
            closeActiveModal(oldId);
        }
    });

    useListen(hotspotButtons, 'click', (e: MouseEvent) => {
        const button = e.currentTarget as HTMLButtonElement;
        const hotspotId = button.getAttribute('aria-controls');
        if (hotspotId) setCurrentHotspotId(hotspotId);

        e.stopPropagation();
    });

    useListen(leftControl, 'click', () => {
        containerScrollPosition -= SCROLL_INCREMENT;
        viewport.current?.scrollTo({ left: containerScrollPosition, behavior: 'smooth' });
    });

    useListen(rightControl, 'click', () => {
        containerScrollPosition += SCROLL_INCREMENT;
        viewport.current?.scrollTo({ left: containerScrollPosition, behavior: 'smooth' });
    });

    useListen(viewport, 'scroll', () => {
        if (!viewport.current) return;
        const { scrollLeft, scrollWidth, clientWidth } = viewport.current;
        const isAtStart = scrollLeft === 0;
        const isAtEnd = scrollLeft + clientWidth === scrollWidth;

        isAtStart
            ? leftControl.current?.setAttribute('disabled', 'true')
            : leftControl.current?.removeAttribute('disabled');

        isAtEnd
            ? rightControl.current?.setAttribute('disabled', 'true')
            : rightControl.current?.removeAttribute('disabled');

        containerScrollPosition = scrollLeft;
    });

    useListen(modalCloseButtons, 'click', (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setCurrentHotspotId(null);
    });

    useListen(document, 'keydown', (e: KeyboardEvent) => {
        if (e.key === 'Escape' && activeDialog) {
            setCurrentHotspotId(null);
        }
    });

    function initializeImageDimensions() {
        const image = imageElement.current;
        if (!image) return;

        const storeImageDimensions = () => {
            imageNaturalWidth = image.naturalWidth;
            imageNaturalHeight = image.naturalHeight;

            if (imageNaturalWidth && imageNaturalHeight) {
                resizeAndAlignImage();
            }
        };

        if (image.complete) storeImageDimensions();
        image.addEventListener('load', storeImageDimensions);
    }

    function openHotspotModal(hotspotId: string) {
        closeActiveModal();

        const targetDialog = Array.from(infoDialogs.current).find(
            (dialog: HTMLDialogElement) => dialog.id === hotspotId,
        );
        if (!targetDialog) return;

        activeDialog = targetDialog;
        addModalBackgroundState();
        updateAriaLabels();

        if (deviceWidth.isMobilePlusAndSmaller()) {
            elements.html.classList.add(MODAL_HTML_CLASS);
            targetDialog.showModal();
        } else {
            targetDialog.show();
            const { top } = element.getBoundingClientRect();
            const scrollOffset = window.scrollY + top - getDeviceHeaderHeight();
            window.scrollTo({ top: scrollOffset, behavior: 'smooth' });
        }

        requestAnimationFrame(() => {
            targetDialog.classList.add(MODAL_OPEN_CLASS);
            targetDialog.querySelector('button')?.focus({ preventScroll: true });
        });
    }

    function closeActiveModal(hotspotId: string | null = null) {
        if (!activeDialog) return;
        activeDialog.classList.remove(MODAL_OPEN_CLASS);

        const focusHotspot = () => {
            if (!hotspotId) return;

            element
                .querySelector<HTMLButtonElement>(`[aria-controls="${hotspotId}"]`)
                ?.focus({ preventScroll: true });
        };

        setTimeout(() => {
            if (!activeDialog) return;

            if (deviceWidth.isMobilePlusAndSmaller()) {
                elements.html.classList.remove(MODAL_HTML_CLASS);
            }

            activeDialog.close();
            activeDialog = null;

            removeModalBackgroundState();
            focusHotspot();
        }, ANIMATION_DURATION);
    }

    function updateAriaLabels() {
        const buttons = Array.from(hotspotButtons.current);

        buttons.forEach((button) => {
            if (!button) return;
            const hotspotId = button.getAttribute('aria-controls');
            const isActive = hotspotId === element.currentHotspotId;

            button.setAttribute('aria-expanded', isActive && activeDialog ? 'true' : 'false');
        });
    }

    function resizeAndAlignImage() {
        const isMobile = deviceWidth.isMobilePlusAndSmaller();

        if (!imageNaturalWidth || !imageNaturalHeight) return;
        const imageAspectRatio = imageNaturalWidth / imageNaturalHeight;

        const container = contentContainer.current;
        const viewportEl = viewport.current;

        if (!container || !viewportEl) return;
        const { width: viewportWidth, height: viewportHeight } = getSafeViewportDimensions();
        const viewportAspectRatio = viewportWidth / viewportHeight;
        const imageWiderThanViewport = imageAspectRatio > viewportAspectRatio;

        let requiredWidth: number;
        if (imageWiderThanViewport && !isMobile) {
            requiredWidth = viewportWidth;
        } else {
            requiredWidth = viewportHeight * imageAspectRatio;
        }

        container.style.width = `${requiredWidth}px`;
        const centerPosition = Math.abs(requiredWidth - viewportWidth) / 2;
        containerScrollPosition = centerPosition;

        if (imageWiderThanViewport) {
            viewportEl.scrollLeft = centerPosition;
            container.style.left = '0';
            element.classList.remove(DISABLE_CONTROLS_CLASS);
        } else {
            viewportEl.scrollLeft = 0;
            container.style.left = `${centerPosition}px`;
            element.classList.add(DISABLE_CONTROLS_CLASS);
        }
    }

    function addModalBackgroundState() {
        element.classList.add(HAS_OPEN_MODAL_CLASS);
        viewport.current?.addEventListener('click', onBackgroundClick);
        hotspotOverlay.current?.setAttribute('inert', '');

        leftControl.current?.setAttribute('inert', '');
        rightControl.current?.setAttribute('inert', '');
    }

    function removeModalBackgroundState() {
        element.classList.remove(HAS_OPEN_MODAL_CLASS);
        viewport.current?.removeEventListener('click', onBackgroundClick);
        hotspotOverlay.current?.removeAttribute('inert');

        leftControl.current?.removeAttribute('inert');
        rightControl.current?.removeAttribute('inert');
    }

    function onBackgroundClick(e: MouseEvent) {
        // Only close if the click didn't happen inside a modal
        const target = e.target as HTMLElement;

        if (!target || target.closest('.hotspot-image__modal')) return;
        if (activeDialog) setCurrentHotspotId(null);
    }
};

HotspotImage.props = {
    currentHotspotId: {
        type: String,
        reflectToAttr: true,
    },
};

defineElement('bpd-hotspot-image', HotspotImage);
